import { useId } from 'react'
import { Shareability } from '../../../types/supabase'
import { parseDollarAmount } from '../../../lib/util/parse'
import { tryParseDecimal } from '../../../lib/util/tryParseDecimal'
import { Tooltip } from '../tooltip'

/**
 * The payment-plan / recurring-medication / shareability capture fields, shared
 * between the new- and edit-expense forms. Spread this fragment into each form's
 * model so both forms write the same expense columns. See the Plaid matcher phase-2 plan.
 */
export type PaymentPlanFieldsModel = {
  is_payment_plan?: boolean
  payment_plan_total?: string | null
  expected_installment_amount?: string | null
  payment_plan_cadence?: number | null
  is_recurring_medication?: boolean
  shareability?: Shareability
}

export type PaymentPlanFieldsValidationErrors = Partial<Record<keyof PaymentPlanFieldsModel, string>>

/**
 * Cadence is stored as a day-count so the matcher can compare it directly to the
 * gap between Plaid charges. 30 ≈ monthly, which keeps parity with the matcher's
 * original fixed monthly window.
 */
export const DEFAULT_PAYMENT_PLAN_CADENCE_DAYS = 30
const CADENCE_OPTIONS: Array<{ label: string; days: number }> = [
  { label: 'Every 2 weeks', days: 14 },
  { label: 'Monthly', days: 30 },
  { label: 'Quarterly', days: 90 },
]

export const SHAREABILITY_HELP_TEXT =
  'Some expenses aren\'t eligible for sharing — check your ministry\'s guidelines. For example, ' +
  'CHM shares prescriptions only at the Gold level and only when related to an eligible incident ' +
  'within 90 days, and maintenance or over-the-counter medications are generally not shareable.'

export function validatePaymentPlanFields(model: PaymentPlanFieldsModel): PaymentPlanFieldsValidationErrors {
  const errors: PaymentPlanFieldsValidationErrors = {}

  // Force the member to resolve an unknown shareability (e.g. a pharmacy-coded draft) before saving.
  if (model.shareability === 'unknown') {
    errors.shareability = 'Please indicate whether this expense is shareable with your sharing ministry.'
  }

  if (!model.is_payment_plan) {
    return errors
  }

  const total = tryParseDecimal(model.payment_plan_total)
  const installment = tryParseDecimal(model.expected_installment_amount)

  if (!total || total.lessThanOrEqualTo(0)) {
    errors.payment_plan_total = 'Please enter the total amount of the payment plan'
  }
  if (!installment || installment.lessThanOrEqualTo(0)) {
    errors.expected_installment_amount = 'Please enter the amount of each payment'
  }
  if (total && installment && installment.greaterThan(total)) {
    errors.expected_installment_amount = 'Each payment cannot be larger than the total'
  }
  return errors
}

export interface PaymentPlanFieldsProps {
  value: PaymentPlanFieldsModel
  onChange: (patch: Partial<PaymentPlanFieldsModel>) => void
  validationErrors?: PaymentPlanFieldsValidationErrors
  disabled?: boolean
}

export function PaymentPlanFields({
  value,
  onChange,
  validationErrors,
  disabled,
}: PaymentPlanFieldsProps) {
  // Unique per form instance so ids/names never collide if two expense forms are mounted.
  const idPrefix = useId()
  return (
    <div className="payment-plan-fields col-12 mt-2">
      <div className="form-check">
        <input className="form-check-input" type="checkbox" id="is_payment_plan"
          checked={!!value.is_payment_plan}
          disabled={disabled}
          onChange={(e) => onChange({ is_payment_plan: e.target.checked })} />
        <label className="form-check-label" htmlFor="is_payment_plan">
          This expense is paid in installments (payment plan)
        </label>
      </div>

      {value.is_payment_plan && (
        <>
          <div className="input-group has-validation mt-2">
            <label className="input-group-text">Total</label>
            <input name="payment_plan_total" type="text"
              className={`form-control ${validationErrors?.payment_plan_total ? 'is-invalid' : ''}`}
              value={`$${value.payment_plan_total || ''}`}
              pattern="\$\d+(\.\d{0,2})?"
              disabled={disabled}
              onChange={(e) => onChange({ payment_plan_total: (e.target.value && parseDollarAmount(e.target.value)) || '' })} />
            {validationErrors?.payment_plan_total &&
              <div className="invalid-feedback show">{validationErrors.payment_plan_total}</div>}
          </div>

          <div className="input-group has-validation mt-2">
            <label className="input-group-text">Each payment</label>
            <input name="expected_installment_amount" type="text"
              className={`form-control ${validationErrors?.expected_installment_amount ? 'is-invalid' : ''}`}
              value={`$${value.expected_installment_amount || ''}`}
              pattern="\$\d+(\.\d{0,2})?"
              disabled={disabled}
              onChange={(e) => onChange({ expected_installment_amount: (e.target.value && parseDollarAmount(e.target.value)) || '' })} />
            {validationErrors?.expected_installment_amount &&
              <div className="invalid-feedback show">{validationErrors.expected_installment_amount}</div>}
          </div>

          <div className="input-group mt-2">
            <label className="input-group-text">How often</label>
            <select name="payment_plan_cadence" className="form-select"
              value={value.payment_plan_cadence ?? DEFAULT_PAYMENT_PLAN_CADENCE_DAYS}
              disabled={disabled}
              onChange={(e) => onChange({ payment_plan_cadence: parseInt(e.target.value, 10) })}>
              {CADENCE_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>{o.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className="form-check mt-3">
        <input className="form-check-input" type="checkbox" id="is_recurring_medication"
          checked={!!value.is_recurring_medication}
          disabled={disabled}
          onChange={(e) => onChange({ is_recurring_medication: e.target.checked })} />
        <label className="form-check-label" htmlFor="is_recurring_medication">
          This is a recurring medication or prescription
        </label>
      </div>

      <div className="shareability-field mt-3" role="radiogroup" aria-labelledby={`${idPrefix}_shareability_label`}>
        {/* Not a <label>: it labels no single control, and wrapping the tooltip trigger in a
            label makes iOS WebKit route taps on the question to the tooltip. */}
        <div className="form-label mb-1" id={`${idPrefix}_shareability_label`}>
          Is this expense shareable with your sharing ministry?
          <Tooltip className="ms-1" tooltip={SHAREABILITY_HELP_TEXT} />
        </div>
        <div className="form-check">
          <input className={`form-check-input ${validationErrors?.shareability ? 'is-invalid' : ''}`}
            type="radio" name={`${idPrefix}_shareability`} id={`${idPrefix}_shareability_shareable`} value="shareable"
            checked={value.shareability === 'shareable'}
            disabled={disabled}
            onChange={(e) => { if (e.target.checked) { onChange({ shareability: 'shareable' }) } }} />
          <label className="form-check-label" htmlFor={`${idPrefix}_shareability_shareable`}>Yes, it is shareable</label>
        </div>
        <div className="form-check">
          <input className={`form-check-input ${validationErrors?.shareability ? 'is-invalid' : ''}`}
            type="radio" name={`${idPrefix}_shareability`} id={`${idPrefix}_shareability_not_shareable`} value="not_shareable"
            checked={value.shareability === 'not_shareable'}
            disabled={disabled}
            onChange={(e) => { if (e.target.checked) { onChange({ shareability: 'not_shareable' }) } }} />
          <label className="form-check-label" htmlFor={`${idPrefix}_shareability_not_shareable`}>No, it is not shareable</label>
        </div>
        {validationErrors?.shareability &&
          <div className="invalid-feedback d-block">{validationErrors.shareability}</div>}
      </div>
    </div>
  )
}
